import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import QboxmailApi from "../qboxmailApi";

/**
 * @type {readonly string[]}
 */
export const apiMethods = Object.getOwnPropertyNames(QboxmailApi.prototype).filter((name) => name !== "constructor");

/**
 * @typedef {typeof apiMethods[number]} ApiMethod
 */

/**
 * Hook to perform API queries, with optional pagination and automatic re-fetching.
 *
 * @param {ApiMethod} method – Name of the QboxmailApi method to invoke.
 * @param {Object} [options] – Optional settings.
 * @param {boolean} [options.enablePagination=false] – If true, expects the response to include `resources` and `pagination` fields.
 * @param {boolean} [options.fetchOnMount=false] – If true, triggers an initial fetch on component mount and when `initialParams` changes.
 * @param {boolean} [options.onlyOnMount=false] – If true, triggers fetch ONLY on component mount, ignoring location.search changes.
 * @param {string} [options.requestParams] - the parameters passed to the api method
 * @param {{ shouldRefresh: (data) => boolean , interval : number}} [options.autoRefresh] – Configures automatic re-fetching.<br/>
 *   • **Object form**: `{ shouldRefresh, interval }`<br/>
 * @param {function():boolean} [options.autoRefresh.shouldRefresh] – (object form) Callback that returns a boolean to determine if auto‑refresh should run.
 * @param {number} [options.autoRefresh.interval=5000] – (object form) Interval in milliseconds for auto‑refresh.
 * @param {*} [options.initialParams] – Initial parameters for the query; defaults to the current location's search string.
 * @returns {{
 *   data: any[]|any,                                 // Response data or array of resources.
 *   pagination: Object,                              // Pagination metadata when enabled.
 *   isLoading: boolean,                              // True while the query is in progress.
 *   isRefreshing: boolean,                           // True while the last query is being re-triggered.
 *   error: Error|null,                               // Error object if the request fails.
 *   getData: function(...any): Promise<any>,         // Manually trigger the query with new parameters.
 *   refreshData: function(): Promise<any>,           // Re-trigger the last query with the previous parameters.
 *   updateData: function(any): void                  // Directly update the `data` state.
 * }}
 */
export function useApiQuery(
  method,
  { enablePagination = false, fetchOnMount = true, onlyOnMount = false, requestParams, autoRefresh = {} } = {}
) {
  const location = useLocation();
  const controllerRef = useRef(null);
  const hasMounted = useRef(false);
  const { interval = 5000, shouldRefresh = () => false } = autoRefresh;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const requestParamsMemoized = useMemo(() => {
    return requestParams;
  }, [requestParams]);

  const getData = useCallback(
    async (params) => {
      controllerRef.current?.abort();
      const actualParams =
        params !== undefined ? (Array.isArray(params) ? params : [params, location.search]) : [location.search];

      const controller = new AbortController();
      controllerRef.current = controller;

      const api = new QboxmailApi({ useCookie: true, signal: controller.signal });

      setIsLoading(true);
      setError(null);

      try {
        const resp = await api[method](...actualParams);

        if (enablePagination) {
          setData(resp.resources || []);
          setPagination(resp.pagination || {});
        } else {
          setData(resp.resources || []);
        }
        setIsLoading(false);
        return resp;
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        if (err.status === 404) {
          setData([]);
          setPagination({});
        } else {
          setError(err);
        }
        setIsLoading(false);
      }
    },
    [method, location.search, enablePagination]
  );

  useEffect(() => {
    const actualParams = requestParamsMemoized ? [requestParamsMemoized, location.search] : [location.search];

    if (!hasMounted.current) {
      hasMounted.current = true;
      if (fetchOnMount) {
        getData(actualParams);
      }
    } else if (!onlyOnMount) {
      getData(actualParams);
    }

    return () => {
      controllerRef.current?.abort();
    };
  }, [getData, requestParamsMemoized, location.search, fetchOnMount, onlyOnMount]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const res = await getData(requestParamsMemoized);

      if (res) {
        // if false has been abort
        setIsRefreshing(false);
      }
      return res;
    } finally {
      setIsRefreshing(false);
    }
  }, [getData, requestParamsMemoized]);

  useEffect(() => {
    const isConditionMet = shouldRefresh(data);
    if (!isConditionMet) return;
    const timer = setInterval(refreshData, interval);
    return () => clearInterval(timer);
  }, [refreshData, interval, data, shouldRefresh]);

  return {
    data,
    updateData: setData,
    pagination,
    isLoading,
    error,
    getData,
    isRefreshing,
    refreshData,
  };
}
